import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Snackbar,
  Stack,
  Tooltip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  AccountBalance as AccountIcon,
  Sync as SyncIcon,
  Info as InfoIcon,
  CreditCard as CreditCardIcon,
  Store as StoreIcon,
  SwapHoriz as TransferIcon,
  Close as CloseIcon,
  UploadFile as UploadFileIcon,
  Link as LinkIcon,
  ReceiptLong as ReceiptLongIcon,
  RequestQuote as RequestQuoteIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { ToastCodeCaption } from '../components/ToastCodeCaption';
import { APP_TOP_TOAST_SNACKBAR_SX } from '../utils/appSnackbarSx';
import { 
  RevolutTransactionComplete, 
  TRANSACTION_TYPE_LABELS, 
  TRANSACTION_STATE_COLORS,
  TRANSACTION_STATE_LABELS
} from '../types/revolut';
import { useUnifiedSync } from '../hooks/useUnifiedSync';
import { categoryChipSx } from '../utils/categoryChipColor';
import { effectiveMerchantName } from '../utils/effectiveMerchant';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  fitTruncateSx,
  fitChipSx,
} from '../utils/fitTable';
import { useSearchParams } from 'react-router-dom';
import { TxDocumentUploadDialog, TxDocumentType } from '../components/TxDocumentUploadDialog';
import { TxLinkExistingDocumentDialog } from '../components/TxLinkExistingDocumentDialog';
import { TxDocumentPreviewDialog } from '../components/TxDocumentPreviewDialog';

interface Transaction {
  id: number;
  date: string;
  description: string;
  reference?: string;
  category?: string;
  amount: number;
  currency: string;
  balance?: number;
  import_batch_id?: string;
  created_at: string;
}

interface TransactionStats {
  total_transactions: number;
  date_range: {
    first_date?: string;
    last_date?: string;
  };
  import_batches: number;
  latest_import?: string;
}

/** Deploy / restart often returns 502/503 for a few seconds; retry before surfacing an error. */
const GATEWAY_RETRY_ATTEMPTS = 3;
const GATEWAY_RETRY_DELAY_MS = 1600;

function isTransientGatewayError(err: unknown): boolean {
  const e = err as { response?: { status?: number }; code?: string };
  const s = e.response?.status;
  return s === 502 || s === 503 || e.code === 'ERR_NETWORK';
}

async function axiosGetWithGatewayRetry<T>(getter: () => Promise<T>): Promise<T> {
  let last: unknown;
  for (let attempt = 0; attempt < GATEWAY_RETRY_ATTEMPTS; attempt++) {
    try {
      return await getter();
    } catch (e) {
      last = e;
      if (!isTransientGatewayError(e) || attempt === GATEWAY_RETRY_ATTEMPTS - 1) {
        throw e;
      }
      await new Promise((r) => setTimeout(r, GATEWAY_RETRY_DELAY_MS));
    }
  }
  throw last;
}

type LinkedTxDocument = {
  link_id?: number;
  document_uid?: string;
  document_type?: string;
  file_name?: string;
  open_url?: string;
} | null;

type LinkedTxDocuments = {
  receipt: LinkedTxDocument;
  sales_invoice: LinkedTxDocument;
  purchase_invoice: LinkedTxDocument;
};

const EMPTY_LINKED_DOCS: LinkedTxDocuments = {
  receipt: null,
  sales_invoice: null,
  purchase_invoice: null,
};

function normalizeLinkedDocs(raw: any): LinkedTxDocuments {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_LINKED_DOCS };
  return {
    receipt: raw.receipt ?? null,
    sales_invoice: raw.sales_invoice ?? null,
    purchase_invoice: raw.purchase_invoice ?? null,
  };
}

function linkedDocsForTx(tx: any, resolvedById: Record<number, LinkedTxDocuments>): LinkedTxDocuments {
  const txId = Number(tx?.id);
  if (Number.isFinite(txId) && Object.prototype.hasOwnProperty.call(resolvedById, txId)) {
    return resolvedById[txId];
  }
  return normalizeLinkedDocs(tx?.documents);
}

/** Prefer the icon's own type, then the purchase invoice (the usual "invoice" on this page). */
function previewDocForAction(
  kind: 'receipt' | 'sales_invoice' | 'purchase_invoice',
  docs: LinkedTxDocuments
): LinkedTxDocument {
  if (kind === 'receipt') return docs.receipt || docs.purchase_invoice || docs.sales_invoice;
  if (kind === 'sales_invoice') return docs.sales_invoice || docs.purchase_invoice || docs.receipt;
  return docs.purchase_invoice || docs.sales_invoice || docs.receipt;
}

const Transactions: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<TransactionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [migrationError, setMigrationError] = useState<boolean>(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Revolut transactions
  const [revolutTransactions, setRevolutTransactions] = useState<RevolutTransactionComplete[]>([]);
  const [dataSource, setDataSource] = useState<string>('all'); // 'all', 'api', 'manual'
  const [showRevolutData, setShowRevolutData] = useState(false); // Will be managed by dataSource changes
  const [selectedTransaction, setSelectedTransaction] = useState<RevolutTransactionComplete | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  const [autoSyncEnabled] = useState(() => {
    // Default to true if not set (enable auto-sync by default)
    const stored = localStorage.getItem('revolut_auto_sync');
    return stored === null ? true : stored === 'true';
  });
  const [deletingTransactions, setDeletingTransactions] = useState(false);
  const [deletingIncomplete, setDeletingIncomplete] = useState(false);
  const [runningMigrations, setRunningMigrations] = useState(false);
  
  // Progress modal state
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [revolutPartialSyncWarning, setRevolutPartialSyncWarning] = useState<string | null>(null);
  const [indeterminateSyncToastOpen, setIndeterminateSyncToastOpen] = useState(false);
  
  // Revolut filters (declared before useUnifiedSync hook)
  const [revolutType, setRevolutType] = useState<string>('');
  const [revolutState, setRevolutState] = useState<string>('');
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  
  const onRevolutSyncStarted = useCallback((data: Record<string, unknown>) => {
    const w = data.warning as string | undefined;
    if (w) {
      setRevolutPartialSyncWarning(w);
      return;
    }
    const failed = data.failed_accounts as
      | Array<{ account?: string; error?: string; error_code?: string }>
      | undefined;
    if (Array.isArray(failed) && failed.length > 0) {
      const lines = failed
        .map((f) => `${f.account ?? '?'} (${f.error_code ?? 'error'})`)
        .join('; ');
      setRevolutPartialSyncWarning(
        `Some Revolut accounts were skipped after the connection check — only the remaining accounts will sync. Skipped: ${lines}. If UK is listed, set Revolut UK keys/tokens on the API host (Hetzner production env) and sync again.`
      );
      return;
    }
    setRevolutPartialSyncWarning(null);
  }, []);

  // Unified sync hook - handles both historical and incremental sync
  // Always sync both accounts ('all') on page load/refresh, regardless of filter selection
  const {
    syncStatus,
    syncProgress,
    isSyncing,
    syncType,
    startSync,
    stopSync,
    error: syncHookError,
  } = useUnifiedSync({
    dataType: 'transactions',
    account: 'all', // Always sync both Ireland and UK accounts automatically
    autoSyncOnMount: autoSyncEnabled, // Always auto-sync when enabled, regardless of dataSource
    autoSyncDelay: 500,
    onSyncStarted: onRevolutSyncStarted
  });
  const canTxImport = user?.tx_import === true;
  
  // Show progress modal automatically when sync starts
  useEffect(() => {
    if (syncStatus === 'running' && syncProgress?.indeterminate) {
      // Indeterminate sync should be toast-only, never modal.
      setProgressModalOpen(false);
      return;
    }
    if (syncStatus === 'running' && !syncProgress?.indeterminate) {
      setProgressModalOpen(true);
    } else if (
      syncProgress &&
      (syncStatus === 'completed' || syncStatus === 'failed' || syncStatus === 'partial')
    ) {
      setProgressModalOpen(true);
      // Keep modal open briefly to show completion/failure, then auto-close after a delay
      setTimeout(() => {
        setProgressModalOpen(false);
      }, 3000);
    }
  }, [syncProgress, syncStatus]);

  // Indeterminate transaction sync: show compact manual-dismiss toast instead of verbose modal text
  useEffect(() => {
    if (syncStatus === 'running' && syncProgress?.indeterminate) {
      setIndeterminateSyncToastOpen(true);
      return;
    }
    setIndeterminateSyncToastOpen(false);
  }, [syncStatus, syncProgress?.indeterminate]);

  // Keep parent Revolut page pills informed so they can show "Sync in progress..."
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('revolut-transactions-sync-state', {
        detail: { running: syncStatus === 'running' },
      })
    );
  }, [syncStatus]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [revolutTotalCount, setRevolutTotalCount] = useState(0); // Store total from Revolut API
  const [manualTotalCount, setManualTotalCount] = useState(0); // Store total from manual transactions API
  const [pageSize, setPageSize] = useState(100);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const focusRevolutId = (searchParams.get('focus_revolut_id') || '').trim();
  const focusAccountFromUrl = (searchParams.get('focus_account') || '').trim().toLowerCase();
  // Deep link from the daily email: ?action=upload[&doc_type=receipt|sales_invoice|purchase_invoice]
  const focusAction = (searchParams.get('action') || '').trim().toLowerCase();
  const focusDocTypeRaw = (searchParams.get('doc_type') || '').trim().toLowerCase();
  const focusDocType: TxDocumentType =
    focusDocTypeRaw === 'sales_invoice' || focusDocTypeRaw === 'purchase_invoice'
      ? focusDocTypeRaw
      : 'receipt';

  // Per-transaction document upload (receipt/invoice) dialog target.
  const [docUploadTx, setDocUploadTx] = useState<RevolutTransactionComplete | null>(null);
  const [docLinkTx, setDocLinkTx] = useState<RevolutTransactionComplete | null>(null);
  const [docUploadSuccess, setDocUploadSuccess] = useState('');
  const [resolvedDocsByTxId, setResolvedDocsByTxId] = useState<Record<number, LinkedTxDocuments>>({});
  const [previewDoc, setPreviewDoc] = useState<LinkedTxDocument>(null);
  const autoUploadHandledRef = useRef(false);

  const refreshResolvedDocs = useCallback(async (txs: any[]) => {
    const ids = Array.from(
      new Set((txs || []).map((t) => Number(t?.id)).filter((id) => Number.isFinite(id) && id > 0))
    );
    const revolutIds = Array.from(
      new Set((txs || []).map((t) => String(t?.revolut_id || '').trim()).filter(Boolean))
    );
    if (ids.length === 0 && revolutIds.length === 0) {
      return;
    }
    try {
      const params: Record<string, string> = {};
      if (ids.length) params.transaction_ids = ids.join(',');
      if (revolutIds.length) params.revolut_ids = revolutIds.join(',');
      const res = await api.get('/api/transaction-documents/resolved', { params });
      const rows = Array.isArray(res.data?.rows) ? res.data.rows : [];
      const next: Record<number, LinkedTxDocuments> = {};
      rows.forEach((row: any) => {
        const txId = Number(row?.transaction_id);
        if (!Number.isFinite(txId)) return;
        next[txId] = normalizeLinkedDocs(row?.documents);
      });
      setResolvedDocsByTxId((prev) => ({ ...prev, ...next }));
    } catch {
      // Keep payload-seeded / previously resolved docs rather than wiping the row grey.
    }
  }, []);

  useEffect(() => {
    if (!focusRevolutId) return;
    setDataSource('api');
    setShowRevolutData(true);
    setCurrentPage(1);
    setSearchTerm(focusRevolutId);
    if (focusAccountFromUrl === 'ireland' || focusAccountFromUrl === 'uk' || focusAccountFromUrl === 'all') {
      setSelectedAccount(focusAccountFromUrl);
    }
  }, [focusRevolutId, focusAccountFromUrl]);

  // When arriving via the email "Upload Doc" button, auto-open the upload dialog for the
  // focused transaction once its row has loaded from the Revolut API.
  useEffect(() => {
    if (autoUploadHandledRef.current) return;
    if (focusAction !== 'upload' || !focusRevolutId) return;
    if (!revolutTransactions || revolutTransactions.length === 0) return;
    const match = revolutTransactions.find(
      (t) => String((t as any).revolut_id || '').trim() === focusRevolutId
    );
    if (match) {
      autoUploadHandledRef.current = true;
      setDocUploadTx(match);
    }
  }, [focusAction, focusRevolutId, revolutTransactions]);

  const txUploadDateIso = (tx: any): string => {
    const raw = tx?.completed_at || tx?.display_date || tx?.created_at || tx?.created_at_revolut;
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };


  // Listen for sync event from parent Revolut page
  useEffect(() => {
    const handleSyncAll = async () => {
      // Auto-sync is now handled by useUnifiedSync hook - no need to call manually
    };

    window.addEventListener('revolut-sync-all', handleSyncAll);

    return () => {
      window.removeEventListener('revolut-sync-all', handleSyncAll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - sync event handler doesn't need dependencies

  useEffect(() => {
    if (dataSource === 'all') {
      // Fetch both types of data when "All" is selected
      fetchRevolutTransactions();
    fetchTransactions();
    fetchStats();
    } else if (showRevolutData) {
      fetchRevolutTransactions();
    } else {
      fetchTransactions();
      fetchStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchTerm, startDate, endDate, sortBy, sortOrder, revolutType, revolutState, selectedAccount]);

  // Maintain the same view when page size changes
  // Store the previous page size to calculate the correct new page
  const prevPageSizeRef = useRef(pageSize);
  
  useEffect(() => {
    if (prevPageSizeRef.current !== pageSize) {
      // Calculate the first item index currently visible with the old page size
      const firstVisibleItemIndex = (currentPage - 1) * prevPageSizeRef.current;
      // Calculate which page this item would be on with the new page size
      const newPage = Math.max(1, Math.floor(firstVisibleItemIndex / pageSize) + 1);
      setCurrentPage(newPage);
      prevPageSizeRef.current = pageSize;
    }
  }, [pageSize, currentPage]);

  // Fetch data when switching data sources or account filter
  useEffect(() => {
    if (dataSource === 'all') {
      fetchRevolutTransactions();
      fetchTransactions();
      fetchStats();
    } else if (dataSource === 'api' && showRevolutData) {
      fetchRevolutTransactions();
    } else if (dataSource === 'manual' && !showRevolutData) {
      fetchTransactions();
      fetchStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, selectedAccount]); // Also run when account filter changes

  // Update pagination when data changes for "All" view
  useEffect(() => {
    if (dataSource === 'all') {
      // Use the actual totals from API responses, not array lengths
      const combinedTotal = revolutTotalCount + manualTotalCount;
      setTotalCount(combinedTotal);
      setTotalPages(Math.ceil(combinedTotal / pageSize));
    }
  }, [dataSource, revolutTotalCount, manualTotalCount, pageSize]);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await axiosGetWithGatewayRetry(() =>
        api.get(`/api/transactions/?${params.toString()}`)
      );

      const txs = Array.isArray(response.data?.transactions) ? response.data.transactions : [];
      const pagination = response.data?.pagination ?? {};
      const pageCountRaw = Number(pagination.pages);
      const totalRaw = Number(pagination.total);
      const safeTotal = Number.isFinite(totalRaw) && totalRaw >= 0 ? totalRaw : txs.length;
      const safePages =
        Number.isFinite(pageCountRaw) && pageCountRaw > 0
          ? pageCountRaw
          : Math.max(1, Math.ceil(safeTotal / pageSize));

      setTransactions(txs);
      setTotalPages(safePages);
      // Always store the manual total count
      setManualTotalCount(safeTotal);
      // Only set totalCount if not viewing 'all' data source
      if (dataSource !== 'all') {
        setTotalCount(safeTotal);
      }
    } catch (err: any) {
      console.error('Error fetching transactions:', err);
      const statusCode = err.response?.status;
      if (statusCode === 502 || statusCode === 503 || err.code === 'ERR_NETWORK') {
        setError(
          'Service temporarily unavailable (502/503). This often happens during a deploy or API restart. Wait a minute and refresh, or try again.'
        );
      } else {
        setError(err.response?.data?.error || 'Failed to load transactions');
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, startDate, endDate, sortBy, sortOrder, dataSource]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/api/transactions/stats');
      setStats(response.data);
    } catch (err: any) {
      console.error('Error fetching transaction stats:', err);
      // Don't set error for stats - it's not critical, just log it
      // Stats endpoint failures shouldn't block the page from loading
    }
  }, []);

  const fetchRevolutTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setMigrationError(false);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: pageSize.toString()
      });

      if (startDate) params.append('from_date', startDate);
      if (endDate) params.append('to_date', endDate);
      if (revolutType) params.append('type', revolutType);
      if (revolutState) params.append('state', revolutState);
      if (searchTerm) params.append('search', searchTerm);
      if (selectedAccount) params.append('account', selectedAccount);
      if (focusRevolutId) params.append('revolut_id', focusRevolutId);

      const response = await axiosGetWithGatewayRetry(() =>
        api.get(`/api/revolut/transactions/complete?${params.toString()}`)
      );

      const txs = Array.isArray(response.data?.transactions) ? response.data.transactions : [];
      const pagination = response.data?.pagination ?? {};
      const pageCountRaw = Number(pagination.total_pages);
      const totalRaw = Number(pagination.total_count);
      const safeTotal = Number.isFinite(totalRaw) && totalRaw >= 0 ? totalRaw : txs.length;
      const safePages =
        Number.isFinite(pageCountRaw) && pageCountRaw > 0
          ? pageCountRaw
          : Math.max(1, Math.ceil(safeTotal / pageSize));

      setRevolutTransactions(txs);
      const seeded: Record<number, LinkedTxDocuments> = {};
      txs.forEach((t: any) => {
        const txId = Number(t?.id);
        if (!Number.isFinite(txId) || !t?.documents) return;
        seeded[txId] = normalizeLinkedDocs(t.documents);
      });
      if (Object.keys(seeded).length) {
        setResolvedDocsByTxId((prev) => ({ ...prev, ...seeded }));
      }
      setTotalPages(safePages);
      // Always store the Revolut total count
      setRevolutTotalCount(safeTotal);
      // Only set totalCount if not viewing 'all' data source
      if (dataSource !== 'all') {
        setTotalCount(safeTotal);
      }
    } catch (err: any) {
      console.error('Error fetching Revolut transactions:', err);
      
      // Check if error is migration-related
      const errorMsg = err.response?.data?.error || '';
      const statusCode = err.response?.status;
      
      if (errorMsg.includes('no such table') || errorMsg.includes('revolut_transactions')) {
        setMigrationError(true);
        setError('Database not ready. The revolut_transactions table is missing. Click "Run Migrations" to initialize the database.');
      } else if (statusCode === 401) {
        setError('Authentication error. Please try refreshing the page or logging in again.');
      } else if (statusCode === 502 || statusCode === 503 || err.code === 'ERR_NETWORK') {
        setError(
          'Service temporarily unavailable (502/503). This often happens during a deploy or API restart. Wait a minute and refresh, or try again.'
        );
      } else {
        setError(errorMsg || 'Failed to load Revolut transactions');
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, startDate, endDate, revolutType, revolutState, searchTerm, selectedAccount, dataSource, focusRevolutId]);

  useEffect(() => {
    void refreshResolvedDocs(revolutTransactions || []);
  }, [revolutTransactions, refreshResolvedDocs]);

  const openLinkedDoc = (doc: LinkedTxDocument) => {
    if (doc?.open_url) setPreviewDoc(doc);
  };

  const onDocActionClick = (
    kind: 'receipt' | 'sales_invoice' | 'purchase_invoice',
    tx: any,
    docs: LinkedTxDocuments
  ) => {
    const preview = previewDocForAction(kind, docs);
    if (preview?.open_url) {
      openLinkedDoc(preview);
      return;
    }
    setDocUploadTx(tx);
  };

  const renderLinkedDocChips = (tx: any) => {
    const docs = linkedDocsForTx(tx, resolvedDocsByTxId);
    return (
      <>
        {docs.purchase_invoice ? (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label="Invoice"
            clickable
            onClick={() => openLinkedDoc(docs.purchase_invoice)}
            sx={{ ...fitChipSx, mr: 0.25 }}
          />
        ) : null}
        {docs.receipt ? (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label="Receipt"
            clickable
            onClick={() => openLinkedDoc(docs.receipt)}
            sx={{ ...fitChipSx, mr: 0.25 }}
          />
        ) : null}
      </>
    );
  };

  const renderRevolutDocActions = (tx: any) => {
    const docs = linkedDocsForTx(tx, resolvedDocsByTxId);
    const receiptPreview = previewDocForAction('receipt', docs);
    const salesPreview = previewDocForAction('sales_invoice', docs);
    const purchasePreview = previewDocForAction('purchase_invoice', docs);
    const hasReceipt = Boolean(docs.receipt);
    const hasSales = Boolean(docs.sales_invoice);
    const hasPurchase = Boolean(docs.purchase_invoice);
    return (
      <>
        {renderLinkedDocChips(tx)}
        <Tooltip
          title={
            hasReceipt
              ? `Receipt linked${docs.receipt?.file_name ? `: ${docs.receipt.file_name}` : ''} — click to preview`
              : receiptPreview
                ? `Invoice linked${receiptPreview.file_name ? `: ${receiptPreview.file_name}` : ''} — click to preview`
                : 'No receipt linked'
          }
        >
          <IconButton
            size="small"
            color={receiptPreview ? 'primary' : 'default'}
            aria-label={
              receiptPreview
                ? `Preview document for TX ${tx.id}`
                : `No receipt linked for TX ${tx.id}`
            }
            onClick={() => onDocActionClick('receipt', tx, docs)}
            sx={receiptPreview ? undefined : { color: 'action.disabled' }}
          >
            <ReceiptLongIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            hasSales
              ? `Sales invoice linked${docs.sales_invoice?.file_name ? `: ${docs.sales_invoice.file_name}` : ''} — click to preview`
              : salesPreview
                ? `Invoice linked${salesPreview.file_name ? `: ${salesPreview.file_name}` : ''} — click to preview`
                : 'No sales invoice linked'
          }
        >
          <IconButton
            size="small"
            color={salesPreview ? 'primary' : 'default'}
            aria-label={
              salesPreview
                ? `Preview document for TX ${tx.id}`
                : `No sales invoice linked for TX ${tx.id}`
            }
            onClick={() => onDocActionClick('sales_invoice', tx, docs)}
            sx={salesPreview ? undefined : { color: 'action.disabled' }}
          >
            <RequestQuoteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            hasPurchase
              ? `Purchase invoice linked${docs.purchase_invoice?.file_name ? `: ${docs.purchase_invoice.file_name}` : ''} — click to preview`
              : purchasePreview
                ? `Invoice linked${purchasePreview.file_name ? `: ${purchasePreview.file_name}` : ''} — click to preview`
                : 'No purchase invoice linked'
          }
        >
          <IconButton
            size="small"
            color={purchasePreview ? 'primary' : 'default'}
            aria-label={
              purchasePreview
                ? `Preview purchase invoice for TX ${tx.id}`
                : `No purchase invoice linked for TX ${tx.id}`
            }
            onClick={() => onDocActionClick('purchase_invoice', tx, docs)}
            sx={purchasePreview ? undefined : { color: 'action.disabled' }}
          >
            <DescriptionIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton
          size="small"
          onClick={() => setDocLinkTx(tx)}
          color="primary"
          title="Link existing document"
          aria-label="Link existing document"
        >
          <LinkIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setDocUploadTx(tx)}
          color="primary"
          title="Upload document (receipt / invoice)"
          aria-label="Upload document"
        >
          <UploadFileIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => showTransactionDetails(tx)} color="primary">
          <InfoIcon fontSize="small" />
        </IconButton>
      </>
    );
  };

  // Refresh data when sync completes (moved after function declarations)
  useEffect(() => {
    if (syncStatus === 'completed' || syncStatus === 'partial') {
      setTimeout(() => {
        if (dataSource === 'all') {
          fetchRevolutTransactions();
          fetchTransactions();
        } else if (dataSource === 'api') {
          fetchRevolutTransactions();
        }
        fetchStats();
        dispatchSyncStatusRefresh();
      }, 1500);
    }
  }, [syncStatus, dataSource, fetchRevolutTransactions, fetchTransactions, fetchStats]);

  const dispatchSyncStatusRefresh = () => {
    window.dispatchEvent(new CustomEvent('revolut-sync-status-refresh'));
  };

  // Unified sync handler - replaces old syncRevolutTransactions and syncHistoricalRevolutTransactions
  const handleUnifiedSync = async () => {
    await startSync();
  };

  // After a manual sync trigger, ensure the list can surface newest data.
  // If the current end-date filter is in the past, clear it so "today" rows
  // are visible without requiring manual filter edits.
  useEffect(() => {
    if (syncStatus !== 'completed' && syncStatus !== 'partial') return;
    if (!endDate) return;
    const today = new Date().toISOString().slice(0, 10);
    if (endDate < today) {
      setEndDate('');
    }
  }, [syncStatus, endDate]);

  // Initial data fetch on page load - auto-sync with Revolut API
  // Only auto-sync when viewing Revolut transactions (dataSource === 'all' or showRevolutData === true)
  useEffect(() => {
    const initialDataFetch = async () => {
      try {
        setLoading(true);
        
        // Auto-sync is now handled by useUnifiedSync hook - no need to call manually
        
        // Fetch all data
        if (dataSource === 'all') {
          await Promise.all([
            fetchRevolutTransactions(),
            fetchTransactions(),
            fetchStats()
          ]);
        } else if (showRevolutData || dataSource === 'api') {
          await fetchRevolutTransactions();
        } else {
          await Promise.all([
            fetchTransactions(),
            fetchStats()
          ]);
        }
        
        // Refresh sync status after initial data fetch to ensure it's up-to-date
        // Add a small delay to ensure backend has updated last_sync_datetime
        setTimeout(() => {
          dispatchSyncStatusRefresh();
        }, 2000);
      } catch (error) {
        console.error('Error during initial data fetch:', error);
        // Still try to fetch existing data even if sync fails
        if (dataSource === 'all') {
          await Promise.all([
            fetchRevolutTransactions(),
            fetchTransactions(),
            fetchStats()
          ]);
        } else if (showRevolutData || dataSource === 'api') {
          await fetchRevolutTransactions();
        } else {
          await Promise.all([
            fetchTransactions(),
            fetchStats()
          ]);
        }
      }
    };
    
    initialDataFetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on initial load

  const runMigrations = async () => {
    try {
      setError('');
      setRunningMigrations(true);
      
      const response = await api.post('/api/migrations/apply');
      
      if (response.data.success) {
        alert('✅ Migrations completed successfully!\n\nThe database has been initialized. You can now sync and view Revolut transactions.');
        setMigrationError(false);
        fetchRevolutTransactions(); // Retry fetching
      } else {
        alert(`❌ Migration failed:\n\n${response.data.error || 'Unknown error'}\n\nPlease contact support.`);
      }
    } catch (err: any) {
      console.error('Error running migrations:', err);
      if (err.response?.status === 403) {
        alert('❌ Unauthorized: You must be an admin to run migrations.\n\nPlease contact your system administrator.');
      } else {
        alert(`❌ Error running migrations:\n\n${err.response?.data?.error || err.message}\n\nPlease try again or contact support.`);
      }
    } finally {
      setRunningMigrations(false);
    }
  };

  const showTransactionDetails = (transaction: RevolutTransactionComplete) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };


  const handleFileUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/api/transactions/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadDialogOpen(false);
      setSelectedFile(null);
      
      // Refresh data
      await fetchTransactions();
      await fetchStats();
      
      alert(`✅ Successfully imported ${response.data.imported_count} transactions!`);
      
    } catch (err: any) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteTransaction = async (transactionId: number) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }

    try {
      await api.delete(`/api/transactions/${transactionId}`);
      await fetchTransactions();
      await fetchStats();
    } catch (err: any) {
      console.error('Error deleting transaction:', err);
      setError(err.response?.data?.error || 'Failed to delete transaction');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL transactions? This action cannot be undone.')) {
      return;
    }

    try {
      await api.post('/api/transactions/clear');
      await fetchTransactions();
      await fetchStats();
      alert('✅ All transactions cleared successfully!');
    } catch (err: any) {
      console.error('Error clearing transactions:', err);
      setError(err.response?.data?.error || 'Failed to clear transactions');
    }
  };

  const deleteAllRevolutTransactions = async () => {
    if (!window.confirm('Are you sure you want to delete ALL Revolut transactions? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingTransactions(true);
      setError('');

      // Use the bulk delete endpoint
      const response = await api.post('/api/revolut/transactions/clear');
      
      console.log('Delete response:', response.data);
      alert(response.data.message || response.data.success ? 'Successfully deleted Revolut transactions and reset sync status' : 'Successfully deleted Revolut transactions');

      // Refresh data
      if (dataSource === 'all') {
        await fetchRevolutTransactions();
        await fetchTransactions();
      } else if (dataSource === 'api') {
        await fetchRevolutTransactions();
      }

      // Refresh sync status to show "Not synced yet" after deletion
      window.dispatchEvent(new CustomEvent('revolut-sync-status-refresh'));
      // Also refresh after a delay to ensure backend has updated
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('revolut-sync-status-refresh'));
      }, 1000);

    } catch (err) {
      console.error('Error deleting transactions:', err);
      setError('Failed to delete transactions');
    } finally {
      setDeletingTransactions(false);
    }
  };

  /** Remove non-final Revolut rows (state "unknown") so a later sync can re-import when finalized */
  const deleteIncompleteUnknownRevolut = async () => {
    const unknownLike = (revolutTransactions || []).filter((tx) => {
      const s = String((tx as any)?.state ?? '').trim().toLowerCase();
      return !s || s === 'unknown';
    });
    const candidateIds = unknownLike.slice(0, 2).map((tx) => Number((tx as any)?.id)).filter((n) => Number.isFinite(n));

    if (
      !window.confirm(
        'Remove the most recent Revolut transactions that are still in state "unknown" (not yet finalized by Revolut)?\n\n' +
          'This does not clear your full history. After deploy, use Sync Transactions to pull them again when they are complete.'
      )
    ) {
      return;
    }
    try {
      setDeletingIncomplete(true);
      setError('');
      const payload: Record<string, unknown> = { account: 'all' };
      if (candidateIds.length > 0) {
        payload.transaction_ids = candidateIds;
      } else {
        payload.limit = 2;
      }
      const response = await api.post('/api/revolut/transactions/delete-incomplete', payload);
      alert(
        (response.data as { message?: string; deleted_count?: number }).message ||
          `Removed ${(response.data as { deleted_count?: number }).deleted_count ?? 0} row(s). Run Sync Transactions.`
      );
      if (dataSource === 'all') {
        await fetchRevolutTransactions();
        await fetchTransactions();
      } else if (dataSource === 'api') {
        await fetchRevolutTransactions();
      }
      await fetchStats();
      window.dispatchEvent(new CustomEvent('revolut-sync-status-refresh'));
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setError(ax.response?.data?.error || 'Failed to remove incomplete transactions');
    } finally {
      setDeletingIncomplete(false);
    }
  };

  const GBP_TO_EUR = 1.16;
  const CURRENCY_SYMBOLS: { [key: string]: string } = {
      'EUR': '€',
      'GBP': '£',
      'USD': '$',
      'JPY': '¥',
      'CHF': 'CHF',
      'CAD': 'CA$',
      'AUD': 'A$',
      'NZD': 'NZ$',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'PLN': 'zł',
      'CZK': 'Kč',
      'HUF': 'Ft',
      'RON': 'lei',
      'BGN': 'лв',
      'HRK': 'kn',
      'RUB': '₽',
      'TRY': '₺',
      'CNY': '¥',
      'INR': '₹',
      'BRL': 'R$',
      'ZAR': 'R',
      'MXN': 'MX$',
      'KRW': '₩',
      'SGD': 'S$',
      'HKD': 'HK$',
      'THB': '฿',
      'MYR': 'RM',
      'IDR': 'Rp',
      'PHP': '₱',
      'VND': '₫',
      'ILS': '₪',
      'AED': 'د.إ',
      'SAR': '﷼',
      'EGP': 'E£',
      'NGN': '₦',
      'KES': 'KSh',
      'GHS': 'GH₵',
      'TZS': 'TSh',
      'UGX': 'USh',
      'ETB': 'Br',
      'MAD': 'د.م.',
      'TND': 'د.ت',
      'DZD': 'د.ج',
      'LBP': 'ل.ل',
      'JOD': 'د.ا',
      'IQD': 'ع.د',
      'KWD': 'د.ك',
      'BHD': 'د.ب',
      'OMR': 'ر.ع.',
      'QAR': 'ر.ق',
      'PKR': '₨',
      'BDT': '৳',
      'LKR': 'Rs',
      'NPR': '₨',
      'MMK': 'K',
      'KHR': '៛',
      'LAK': '₭',
      'BND': 'B$',
      'CLP': '$',
      'COP': '$',
      'PEN': 'S/',
      'ARS': '$',
      'UYU': '$U',
      'PYG': '₲',
      'BOB': 'Bs.',
      'VEF': 'Bs.',
      'GTQ': 'Q',
      'HNL': 'L',
      'NIO': 'C$',
      'CRC': '₡',
      'PAB': 'B/.',
      'DOP': 'RD$',
      'JMD': 'J$',
      'TTD': 'TT$',
      'BBD': 'Bds$',
      'XCD': '$',
      'AWG': 'ƒ',
      'ANG': 'ƒ',
      'SRD': '$',
      'GYD': '$',
      'BSD': '$',
      'BZD': '$',
      'BMD': '$',
      'KYD': '$',
      'FJD': 'FJ$',
      'PGK': 'K',
      'SBD': 'SI$',
      'VUV': 'Vt',
      'WST': 'WS$',
      'TOP': 'T$',
      'XPF': 'F',
      'XOF': 'CFA',
      'XAF': 'FCFA',
      'DJF': 'Fdj',
      'ERN': 'Nfk',
      'FKP': '£',
      'GEL': '₾',
      'GIP': '£',
      'GMD': 'D',
      'GNF': 'FG',
      'HTG': 'G',
      'IRR': '﷼',
      'ISK': 'kr',
      'KGS': 'лв',
      'KMF': 'CF',
      'KPW': '₩',
      'KZT': '₸',
      'LRD': '$',
      'LSL': 'L',
      'LYD': 'د.ل',
      'MDL': 'lei',
      'MGA': 'Ar',
      'MKD': 'ден',
      'MNT': '₮',
      'MOP': 'P',
      'MRU': 'UM',
      'MUR': '₨',
      'MVR': 'Rf',
      'MWK': 'MK',
      'MZN': 'MT',
      'NAD': '$',
      'RSD': 'Дин.',
      'RWF': 'RF',
      'SCR': '₨',
      'SDG': 'ج.س.',
      'SHP': '£',
      'SLE': 'Le',
      'SLL': 'Le',
      'SOS': 'S',
      'SSP': '£',
      'STN': 'Db',
      'SYP': '£',
      'SZL': 'L',
      'TJS': 'SM',
      'TMT': 'T',
      'TVD': '$',
      'TWD': 'NT$',
      'UAH': '₴',
      'UYW': 'UYI',
      'UZS': 'so\'m',
      'VED': 'Bs.S',
      'VES': 'Bs.S',
      'YER': '﷼',
      'ZMW': 'ZK',
      'ZWL': '$',
    };

  const formatCurrency = (amount: number | null | undefined, currency: string = 'EUR', eurEquivalent?: number) => {
    const validCurrency = currency && currency.trim() !== '' ? currency.toUpperCase() : 'EUR';
    const safeAmount = amount === null || amount === undefined || isNaN(amount) ? 0 : amount;
    const symbol = CURRENCY_SYMBOLS[validCurrency] || validCurrency;
    const formattedAmount = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(safeAmount);
    const base = `${symbol}${formattedAmount}`;
    if (validCurrency === 'GBP') {
      const eur = eurEquivalent ?? (safeAmount * GBP_TO_EUR);
      const eurFormatted = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(eur);
      return `${base} (${eurFormatted})`;
    }
    return base;
  };

  /** Renders amount with minus before symbol, secondary currency on new line without brackets */
  const FormattedCurrencyCell = ({ amount, currency = 'EUR', eurEquivalent, color }: { amount: number | null | undefined; currency?: string; eurEquivalent?: number; color?: string }) => {
    const validCurrency = currency && currency.trim() !== '' ? currency.toUpperCase() : 'EUR';
    const safeAmount = amount === null || amount === undefined || isNaN(amount) ? 0 : amount;
    const symbol = CURRENCY_SYMBOLS[validCurrency] || validCurrency;
    const absFormatted = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(safeAmount));
    const primary = safeAmount < 0 ? `-${symbol}${absFormatted}` : `${symbol}${absFormatted}`;
    const isGbp = validCurrency === 'GBP';
    const eur = isGbp ? (eurEquivalent ?? safeAmount * GBP_TO_EUR) : null;
    const eurFormatted = eur != null ? new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(eur) : null;
    return (
      <Box component="span" sx={{ color: color || 'inherit' }}>
        {primary}
        {eurFormatted != null && (
          <Typography component="span" variant="caption" display="block" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            {eurFormatted}
          </Typography>
        )}
      </Box>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const revolutRowTime = (tx: RevolutTransactionComplete | Record<string, unknown>) => {
    const t = tx as Record<string, unknown>;
    let s = t.display_date ?? t.completed_at ?? t.created_at;
    if (s == null || s === '') return new Date(NaN);
    let raw = String(s).trim();
    // ISO datetime without offset was historically ambiguous (UTC in DB → parsed as local). Prefer explicit Z from API.
    if (/^\d{4}-\d{2}-\d{2}T/.test(raw) && !/[zZ]$|[+-]\d{2}:?\d{2}$/.test(raw)) {
      raw = `${raw}Z`;
    }
    return new Date(raw);
  };

  const combinedRowTime = (tx: { _type?: string; revolut_id?: string; date?: string } & Record<string, unknown>) => {
    if (tx._type === 'revolut' || tx.revolut_id) {
      return revolutRowTime(tx);
    }
    return new Date(tx.date || 0);
  };

  const getDateRange = () => {
    // Get the currently displayed transactions for the current page
    let displayedTransactions: any[] = [];
    
    if (dataSource === 'all') {
      const combined = [...revolutTransactions.map(tx => ({ ...tx, _type: 'revolut' })), ...transactions.map(tx => ({ ...tx, _type: 'csv' }))]
        .sort((a, b) => {
          const dateA = combinedRowTime(a as any);
          const dateB = combinedRowTime(b as any);
          return dateB.getTime() - dateA.getTime();
        });
      displayedTransactions = combined.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    } else if (dataSource === 'api') {
      displayedTransactions = revolutTransactions.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    } else if (dataSource === 'manual') {
      displayedTransactions = transactions.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    }

    // Calculate date range from displayed transactions
    if (displayedTransactions.length > 0) {
      const dates = displayedTransactions
        .map(tx => combinedRowTime(tx as any))
        .filter(date => !isNaN(date.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
      
      if (dates.length > 0) {
        const firstDate = formatDate(dates[0].toISOString());
        const lastDate = formatDate(dates[dates.length - 1].toISOString());
        return `${firstDate} - ${lastDate}`;
      }
    }

    // Fallback to overall stats if no displayed transactions
    let firstDate = 'N/A';
    let lastDate = 'N/A';

    if (dataSource === 'api') {
      if (revolutTransactions.length > 0) {
        firstDate = formatDate(
          (revolutTransactions[revolutTransactions.length - 1] as any)?.display_date ||
            revolutTransactions[revolutTransactions.length - 1]?.completed_at ||
            revolutTransactions[revolutTransactions.length - 1]?.created_at
        );
        lastDate = formatDate(
          (revolutTransactions[0] as any)?.display_date || revolutTransactions[0]?.completed_at || revolutTransactions[0]?.created_at
        );
      }
    } else if (dataSource === 'manual') {
      if (stats?.date_range?.first_date && stats?.date_range?.last_date) {
        firstDate = formatDate(stats.date_range.first_date);
        lastDate = formatDate(stats.date_range.last_date);
      }
    } else { // 'all'
      if (stats?.date_range?.first_date && stats?.date_range?.last_date) {
        firstDate = formatDate(stats.date_range.first_date);
        lastDate = formatDate(stats.date_range.last_date);
      } else if (revolutTransactions.length > 0) {
        firstDate = formatDate(
          (revolutTransactions[revolutTransactions.length - 1] as any)?.display_date ||
            revolutTransactions[revolutTransactions.length - 1]?.completed_at ||
            revolutTransactions[revolutTransactions.length - 1]?.created_at
        );
        lastDate = formatDate(
          (revolutTransactions[0] as any)?.display_date || revolutTransactions[0]?.completed_at || revolutTransactions[0]?.created_at
        );
      }
    }

    if (firstDate !== 'N/A' && lastDate !== 'N/A') {
      return `${firstDate} - ${lastDate}`;
    } else if (firstDate !== 'N/A') {
      return firstDate;
    } else if (lastDate !== 'N/A') {
      return lastDate;
    }
    return 'N/A';
  };

  const getFirstTransactionDate = (): string => {
    if (dataSource === 'api') {
      if (revolutTransactions.length > 0) {
        const t = revolutTransactions[revolutTransactions.length - 1];
        return formatDate(
          (t as any).display_date || t.completed_at || t.created_at || ''
        );
      }
    } else if (dataSource === 'manual') {
      if (stats?.date_range?.first_date) return formatDate(stats.date_range.first_date);
      if (transactions.length > 0) {
        const dates = transactions.map(t => new Date(t.date)).filter(d => !isNaN(d.getTime()));
        if (dates.length > 0) return formatDate(new Date(Math.min(...dates.map(d => d.getTime()))).toISOString());
      }
    } else {
      if (stats?.date_range?.first_date) return formatDate(stats.date_range.first_date);
      const allDates: Date[] = [];
      revolutTransactions.forEach(t => {
        const d = revolutRowTime(t);
        if (!isNaN(d.getTime())) allDates.push(d);
      });
      transactions.forEach(t => { const d = new Date(t.date); if (!isNaN(d.getTime())) allDates.push(d); });
      if (allDates.length > 0) return formatDate(new Date(Math.min(...allDates.map(d => d.getTime()))).toISOString());
    }
    return 'N/A';
  };

  const filteredTransactions = transactions.filter(transaction =>
    transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transaction.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transaction.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // RBAC Check - only allow users with transactions permission
  if (!user || (user.role !== 'admin' && !user.can_view_entities)) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          🚫 Access Denied
        </Typography>
        <Typography color="text.secondary">
          You don't have permission to view transactions.
        </Typography>
      </Box>
    );
  }

  const firstDateStr = getFirstTransactionDate();

  return (
    <Box>
      <Card>
        <CardContent>
          {/* Header with inline filters */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            {/* Left side - Title and Data Source */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" component="h2">
              <AccountIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Transactions
            </Typography>
              
            </Box>

            {/* Right side - Action buttons */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* Search Field */}
              <TextField
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 200 }}
              />
              
              {/* Filter Toggle Button */}
              <Button
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterIcon />}
                size="small"
                color="primary"
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              
              {dataSource === 'all' && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleUnifiedSync}
                    startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                    disabled={isSyncing || !canTxImport}
                    color="primary"
                    title={
                      canTxImport
                        ? undefined
                        : 'Transaction import is disabled for your account (missing tx_import capability).'
                    }
                  >
                    {isSyncing ? 'Syncing...' : 'Sync Transactions'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={deleteIncompleteUnknownRevolut}
                    disabled={deletingIncomplete || isSyncing || loading || !canTxImport}
                    color="secondary"
                    size="small"
                    title="Remove latest unknown-state rows so the next sync can re-import when Revolut finalizes them"
                  >
                    {deletingIncomplete ? 'Removing…' : 'Drop unknown TXs'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setUploadDialogOpen(true)}
                    startIcon={<UploadIcon />}
                    color="primary"
                  >
                    Import CSV
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={deleteAllRevolutTransactions}
                    disabled={deletingTransactions || loading}
                    sx={{ ml: 1 }}
                  >
                    {deletingTransactions ? 'Deleting...' : 'Delete Revolut Transactions'}
                  </Button>
                </>
              )}
              {dataSource === 'api' && (
                <>
                  {migrationError ? (
                    <Button
                      variant="contained"
                      onClick={runMigrations}
                      startIcon={runningMigrations ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                      disabled={runningMigrations}
                      color="warning"
                      sx={{ mr: 1 }}
                    >
                      {runningMigrations ? 'Running Migrations...' : 'Run Migrations'}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        onClick={handleUnifiedSync}
                        startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                        disabled={isSyncing || !canTxImport}
                        color="primary"
                        sx={{ mr: 1 }}
                        title={
                          canTxImport
                            ? undefined
                            : 'Transaction import is disabled for your account (missing tx_import capability).'
                        }
                      >
                        {isSyncing ? 'Syncing...' : 'Sync Transactions'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={deleteIncompleteUnknownRevolut}
                        disabled={deletingIncomplete || isSyncing || loading || !canTxImport}
                        color="secondary"
                        size="small"
                        sx={{ mr: 1 }}
                        title="Remove latest unknown-state rows so the next sync can re-import when Revolut finalizes them"
                      >
                        {deletingIncomplete ? 'Removing…' : 'Drop unknown TXs'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={deleteAllRevolutTransactions}
                        disabled={deletingTransactions || loading}
                        sx={{ ml: 1 }}
                      >
                        {deletingTransactions ? 'Deleting...' : 'Delete Revolut Transactions'}
                      </Button>
                    </>
                  )}
                </>
              )}
              {dataSource === 'manual' && (
                <>
              <Button
                variant="outlined"
                onClick={() => setUploadDialogOpen(true)}
                startIcon={<UploadIcon />}
                color="primary"
              >
                Import CSV
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearAll}
                startIcon={<DeleteIcon />}
                color="error"
                disabled={transactions.length === 0}
              >
                Clear All
              </Button>
                </>
              )}
              <Button
                variant="outlined"
                onClick={() => {
                  if (dataSource === 'all') {
                    fetchRevolutTransactions();
                    fetchTransactions();
                  } else if (dataSource === 'api') {
                    fetchRevolutTransactions();
                  } else {
                    fetchTransactions();
                  }
                }}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Filters Row */}
          {showFilters && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Data Source Filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Data Source</InputLabel>
                <Select
                  value={dataSource}
                  onChange={(e) => {
                    const newDataSource = e.target.value;
                    setDataSource(newDataSource);
                    if (newDataSource === 'api') {
                      setShowRevolutData(true);
                    } else if (newDataSource === 'manual') {
                      setShowRevolutData(false);
                    }
                    // For 'all', we keep the current showRevolutData state
                  }}
                  variant="outlined"
                  label="Data Source"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="api">API Sync</MenuItem>
                  <MenuItem value="manual">Manual Upload</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              
              {/* Revolut-specific filters */}
              {(dataSource === 'all' || dataSource === 'api') && (
                <>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Account</InputLabel>
                    <Select
                      value={selectedAccount}
                      label="Account"
                      onChange={(e) => setSelectedAccount(e.target.value)}
                    >
                      <MenuItem value="all">🌍 All Accounts</MenuItem>
                      <MenuItem value="ireland">🇮🇪 Ireland</MenuItem>
                      <MenuItem value="uk">🇬🇧 UK</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={revolutType}
                      label="Type"
                      onChange={(e) => setRevolutType(e.target.value)}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="card_payment">Card Payments</MenuItem>
                      <MenuItem value="transfer">Transfers</MenuItem>
                      <MenuItem value="atm">ATM Withdrawals</MenuItem>
                      <MenuItem value="exchange">Currency Exchange</MenuItem>
                      <MenuItem value="fee">Fees</MenuItem>
                      <MenuItem value="refund">Refunds</MenuItem>
                      <MenuItem value="topup">Top Up</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>State</InputLabel>
                    <Select
                      value={revolutState}
                      label="State"
                      onChange={(e) => setRevolutState(e.target.value)}
                    >
                      <MenuItem value="">All States</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="declined">Declined</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>
          )}



          {(error || syncHookError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || syncHookError}
            </Alert>
          )}
          {revolutPartialSyncWarning && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRevolutPartialSyncWarning(null)}>
              {revolutPartialSyncWarning}
            </Alert>
          )}

          {/* Summary and Pagination Controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            {/* Summary Sentence */}
            <Typography variant="body2" color="text.secondary">
              Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount.toLocaleString()} transactions{firstDateStr !== 'N/A' ? ` since ${firstDateStr}` : ''} ({getDateRange()})
            </Typography>

            {/* Pagination Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Rows per page:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  variant="outlined"
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                </Select>
              </FormControl>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, page) => setCurrentPage(page)}
                  color="primary"
                  showFirstButton
                  showLastButton
                  size="small"
                />
              )}
            </Box>
          </Box>

          {/* Transactions Table */}
          {dataSource === 'all' ? (
            // Combined Transactions Table (All Data Sources)
            <TableContainer component={Paper} variant="outlined" sx={{ ...FIT_TABLE_CONTAINER_SX, maxHeight: '70vh' }}>
              <Table stickyHeader size="small" sx={FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'date', pct: 11 },
                    { key: 'txid', pct: 10 },
                    { key: 'type', pct: 9 },
                    { key: 'state', pct: 8 },
                    { key: 'account', pct: 7 },
                    { key: 'description' },
                    { key: 'amount', pct: 10 },
                    { key: 'balance', pct: 10 },
                    { key: 'actions', pct: 16 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('date')}
                    >
                      <strong>Date {getSortIcon('date')}</strong>
                    </TableCell>
                    <TableCell><strong>TX ID</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>State</strong></TableCell>
                    <TableCell><strong>Account</strong></TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('description')}
                    >
                      <strong>Description {getSortIcon('description')}</strong>
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('amount')}
                    >
                      <strong>Amount {getSortIcon('amount')}</strong>
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('balance')}
                    >
                      <strong>Balance After {getSortIcon('balance')}</strong>
                    </TableCell>
                    <TableCell><strong>Source</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>Loading all transactions...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : [...revolutTransactions, ...transactions].length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary" gutterBottom>
                          No transactions found.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Try syncing your Revolut account or importing CSV data.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    [...revolutTransactions.map(tx => ({ ...tx, _type: 'revolut' })), ...transactions.map(tx => ({ ...tx, _type: 'csv' }))]
                      .sort((a, b) => {
                        const dateA = combinedRowTime(a as any);
                        const dateB = combinedRowTime(b as any);
                        return dateB.getTime() - dateA.getTime();
                      })
                      .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                      .map((tx, index) => (
                        <TableRow key={`${tx._type}-${tx.id}`} hover>
                          <TableCell>
                            <Typography variant="body2" sx={fitTruncateSx}>
                              {(
                                (tx as any)._type === 'revolut' || (tx as any).revolut_id
                                  ? revolutRowTime(tx as RevolutTransactionComplete)
                                  : new Date((tx as any).date)
                              ).toLocaleDateString('en-IE', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              component="span"
                              title={
                                tx._type === 'revolut'
                                  ? String((tx as any).revolut_id || (tx as any).id || '')
                                  : String((tx as any).id ?? '')
                              }
                              sx={{
                                ...fitTruncateSx,
                                fontFamily: 'ui-monospace, monospace',
                                fontSize: '0.75rem',
                              }}
                            >
                              {tx._type === 'revolut'
                                ? String((tx as any).revolut_id || (tx as any).id || '-')
                                : String((tx as any).id ?? '-')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={tx._type === 'revolut' ? (TRANSACTION_TYPE_LABELS[(tx as any).type] || (tx as any).type) : 'Transaction'}
              size="small"
                              sx={fitChipSx}
                              variant="outlined"
                              color={tx._type === 'revolut' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={tx._type === 'revolut' ? ((tx as any).state || 'unknown') : 'N/A'}
                              size="small"
                              sx={fitChipSx}
                              variant="outlined"
                              color={tx._type === 'revolut' ? 
                                ((tx as any).state === 'completed' ? 'success' : 
                                 (tx as any).state === 'declined' ? 'error' : 
                                 (tx as any).state === 'pending' ? 'warning' : 'default') : 
                                'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={tx._type === 'revolut' ? 
                                ((tx as any).merchant_country === 'IRL' || (tx as any).currency === 'EUR' ? 'IRL' : 'UK') : 
                                'N/A'}
                              size="small"
                              sx={fitChipSx}
                              variant="outlined"
                              color={tx._type === 'revolut' ? 
                                ((tx as any).merchant_country === 'IRL' || (tx as any).currency === 'EUR' ? 'success' : 'primary') : 
                                'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                sx={fitTruncateSx}
                                title={tx._type === 'revolut'
                                  ? (effectiveMerchantName(tx as any) || (tx as any).description || 'N/A')
                                  : (tx as any).description}
                              >
                                {tx._type === 'revolut' 
                                  ? (effectiveMerchantName(tx as any) || (tx as any).description || 'N/A')
                                  : (tx as any).description
                                }
                              </Typography>
                              {tx._type === 'revolut' ? (
                                <Box sx={{ mt: 0.25 }}>{renderLinkedDocChips(tx)}</Box>
                              ) : null}
                              {tx._type === 'revolut' && (tx as any).merchant_city && (
                                <Typography variant="caption" color="text.secondary" sx={fitTruncateSx}>
                                  {(tx as any).merchant_city}, {(tx as any).merchant_country}
                                </Typography>
                              )}
                              {(tx as any).reference && (
                                <Typography variant="caption" color="text.secondary" sx={fitTruncateSx} title={String((tx as any).reference)}>
                                  Ref: {(tx as any).reference}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {tx._type === 'revolut' ? (
                                <FormattedCurrencyCell
                                  amount={(tx as any).amount_display}
                                  currency={(tx as any).currency}
                                  color={(tx as any).amount > 0 ? 'success.main' : 'error.main'}
                                />
                              ) : (
                                formatCurrency((tx as any).amount, (tx as any).currency)
                              )}
                            </Typography>
                            {tx._type === 'revolut' && (tx as any).fee_amount && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Fee: {formatCurrency((tx as any).fee_amount_display, (tx as any).fee_currency)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {tx._type === 'revolut' ? (
                                <FormattedCurrencyCell
                                  amount={(tx as any).balance_display}
                                  currency={(tx as any).currency}
                                />
                              ) : (
                                formatCurrency((tx as any).balance, (tx as any).currency)
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={tx._type === 'revolut' ? 'API Sync' : 'Manual Upload'} 
              size="small"
                              sx={fitChipSx}
                              color={tx._type === 'revolut' ? 'primary' : 'secondary'} 
                              variant="outlined"
                              icon={tx._type === 'revolut' ? <SyncIcon fontSize="small" /> : <UploadIcon fontSize="small" />}
                            />
                          </TableCell>
                          <TableCell align="center" className="fit-table-no-clip">
                            {tx._type === 'revolut' ? (
                              renderRevolutDocActions(tx)
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteTransaction((tx as any).id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : showRevolutData ? (
            // Revolut Transactions Table
            <TableContainer component={Paper} variant="outlined" sx={{ ...FIT_TABLE_CONTAINER_SX, maxHeight: '70vh' }}>
              <Table stickyHeader size="small" sx={FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'date', pct: 10 },
                    { key: 'txid', pct: 9 },
                    { key: 'type', pct: 8 },
                    { key: 'state', pct: 7 },
                    { key: 'account', pct: 6 },
                    { key: 'description' },
                    { key: 'amount', pct: 9 },
                    { key: 'balance', pct: 9 },
                    { key: 'card', pct: 7 },
                    { key: 'source', pct: 8 },
                    { key: 'details', pct: 14 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>TX ID</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>State</strong></TableCell>
                    <TableCell><strong>Account</strong></TableCell>
                    <TableCell><strong>Description / Merchant</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell align="right"><strong>Balance After</strong></TableCell>
                    <TableCell><strong>Card</strong></TableCell>
                    <TableCell><strong>Source</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>Loading Revolut transactions...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : revolutTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary" gutterBottom>
                          No Revolut transactions found.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Click "Sync Revolut" to import transactions from your Revolut account.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    revolutTransactions.map((tx) => (
                      <TableRow key={tx.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={fitTruncateSx}>
                            {revolutRowTime(tx).toLocaleDateString('en-IE', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            component="span"
                            title={String(tx.revolut_id || tx.id)}
                            sx={{
                              ...fitTruncateSx,
                              fontFamily: 'ui-monospace, monospace',
                              fontSize: '0.75rem',
                            }}
                          >
                            {tx.revolut_id || String(tx.id)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Chip 
                            label={TRANSACTION_TYPE_LABELS[(tx as any).type] || (tx as any).type}
                            size="small"
                            sx={fitChipSx}
                            variant="outlined"
                            icon={
                              (tx as any).type === 'card_payment' ? <CreditCardIcon fontSize="small" /> :
                              (tx as any).type === 'transfer' ? <TransferIcon fontSize="small" /> :
                              (tx as any).merchant_name ? <StoreIcon fontSize="small" /> : undefined
                            }
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Chip 
                            label={TRANSACTION_STATE_LABELS[tx.state] || tx.state}
                            color={TRANSACTION_STATE_COLORS[tx.state] || 'default'}
                            size="small"
                            sx={fitChipSx}
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Chip 
                            label={(tx as any).merchant_country === 'IRL' || (tx as any).currency === 'EUR' ? 'IRL' : 'UK'}
                            size="small"
                            sx={fitChipSx}
                            variant="outlined"
                            color={(tx as any).merchant_country === 'IRL' || (tx as any).currency === 'EUR' ? 'success' : 'primary'}
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              sx={fitTruncateSx}
                              title={effectiveMerchantName(tx as any) || (tx as any).description || 'N/A'}
                            >
                              {effectiveMerchantName(tx as any) || (tx as any).description || 'N/A'}
                            </Typography>
                            <Box sx={{ mt: 0.25 }}>{renderLinkedDocChips(tx)}</Box>
                            {(tx as any).merchant_city && (
                              <Typography variant="caption" color="text.secondary" sx={fitTruncateSx}>
                                {(tx as any).merchant_city}, {(tx as any).merchant_country}
                              </Typography>
                            )}
                            {(tx as any).reference && (
                              <Typography variant="caption" color="text.secondary" sx={fitTruncateSx} title={String((tx as any).reference)}>
                                Ref: {(tx as any).reference}
                              </Typography>
                            )}
          </Box>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            <FormattedCurrencyCell
                              amount={(tx as any).amount_display}
                              currency={(tx as any).currency}
                              color={(tx as any).amount > 0 ? 'success.main' : 'error.main'}
                            />
                          </Typography>
                          {(tx as any).fee_amount && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Fee: {formatCurrency((tx as any).fee_amount_display, (tx as any).fee_currency)}
                            </Typography>
                          )}
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            <FormattedCurrencyCell
                              amount={(tx as any).balance_display}
                              currency={(tx as any).currency}
                            />
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          {(tx as any).card_number && (
                            <Typography variant="caption" color="text.secondary" sx={fitTruncateSx}>
                              {(tx as any).card_number}
                            </Typography>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          <Chip 
                            label="API Sync" 
                            size="small" 
                            sx={fitChipSx}
                            color="primary" 
                            variant="outlined"
                            icon={<SyncIcon fontSize="small" />}
                          />
                        </TableCell>
                        
                        <TableCell align="center" className="fit-table-no-clip">
                          {renderRevolutDocActions(tx)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            // CSV Transactions Table
          <TableContainer component={Paper} variant="outlined" sx={{ ...FIT_TABLE_CONTAINER_SX, maxHeight: '70vh' }}>
            <Table stickyHeader size="small" sx={FIT_TABLE_SX}>
              <FitColgroup
                cols={[
                  { key: 'date', pct: 12 },
                  { key: 'description' },
                  { key: 'reference', pct: 14 },
                  { key: 'category', pct: 12 },
                  { key: 'amount', pct: 11 },
                  { key: 'balance', pct: 11 },
                  { key: 'source', pct: 11 },
                  { key: 'actions', pct: 7 },
                ]}
              />
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('date')}
                  >
                    <strong>Date {getSortIcon('date')}</strong>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('description')}
                  >
                    <strong>Description {getSortIcon('description')}</strong>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('reference')}
                  >
                    <strong>Reference {getSortIcon('reference')}</strong>
                  </TableCell>
                  <TableCell 
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('category')}
                  >
                    <strong>Category {getSortIcon('category')}</strong>
                  </TableCell>
                  <TableCell 
                    align="right"
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('amount')}
                  >
                    <strong>Amount {getSortIcon('amount')}</strong>
                  </TableCell>
                  <TableCell 
                    align="right"
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('balance')}
                  >
                    <strong>Balance {getSortIcon('balance')}</strong>
                  </TableCell>
                  <TableCell><strong>Source</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                      <Typography sx={{ mt: 2 }}>Loading transactions...</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredTransactions.length === 0 ? (
                  <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No transactions found. Import a CSV file to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={fitTruncateSx}>
                          {formatDate(transaction.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={fitTruncateSx} title={transaction.description}>
                          {transaction.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={fitTruncateSx} title={transaction.reference || ''}>
                          {transaction.reference || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {transaction.category && (
                          <Chip label={transaction.category} size="small" variant="filled" title={transaction.category} sx={{ ...categoryChipSx(transaction.category), ...fitChipSx }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          fontWeight="medium"
                          color={transaction.amount >= 0 ? 'success.main' : 'error.main'}
                        >
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {transaction.balance === null || transaction.balance === undefined
                            ? formatCurrency(0, transaction.currency)
                            : formatCurrency(transaction.balance, transaction.currency)}
                        </Typography>
                      </TableCell>
                        <TableCell>
                          <Chip 
                            label="Manual Upload" 
                            size="small" 
                            sx={fitChipSx}
                            color="secondary" 
                            variant="outlined"
                            icon={<UploadIcon fontSize="small" />}
                          />
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTransaction((transaction as any).id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}

        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Transactions from CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a CSV file exported from Revolut Business Account. The file should contain columns like:
              Type, Product, Started Date, Completed Date, Description, Amount, Fee, Currency, State, Balance
            </Typography>
            
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              style={{ width: '100%', padding: '8px' }}
            />
            
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button 
            onClick={handleFileUpload} 
            variant="contained" 
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Detail Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)} 
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Transaction Details
            </Typography>
            {selectedTransaction && (
              <Chip 
                label={TRANSACTION_STATE_LABELS[selectedTransaction.state] || selectedTransaction.state}
                color={TRANSACTION_STATE_COLORS[selectedTransaction.state] || 'default'}
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedTransaction && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Core Information */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Core Information</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography><strong>Transaction ID:</strong> {selectedTransaction.revolut_id}</Typography>
                <Typography><strong>Type:</strong> {TRANSACTION_TYPE_LABELS[selectedTransaction.type] || selectedTransaction.type}</Typography>
                <Typography><strong>State:</strong> {selectedTransaction.state}</Typography>
                <Typography><strong>Created (Revolut):</strong> {new Date(selectedTransaction.created_at).toLocaleString()}</Typography>
                {selectedTransaction.completed_at && (
                  <Typography><strong>Completed (settled):</strong> {new Date(selectedTransaction.completed_at).toLocaleString()}</Typography>
                )}
                {(selectedTransaction as RevolutTransactionComplete).display_date && (
                  <Typography>
                    <strong>Date in table &amp; date filters:</strong>{' '}
                    {new Date((selectedTransaction as RevolutTransactionComplete).display_date!).toLocaleString()}
                  </Typography>
                )}
              </Box>

              {/* Amount Information */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography>
                  <strong>Amount:</strong>{' '}
                  <FormattedCurrencyCell
                    amount={selectedTransaction.amount_display}
                    currency={selectedTransaction.currency}
                    color={(selectedTransaction.amount ?? 0) > 0 ? 'success.main' : 'error.main'}
                  />
                </Typography>
                {selectedTransaction.balance && (
                  <Typography>
                    <strong>Balance After:</strong>{' '}
                    <FormattedCurrencyCell amount={selectedTransaction.balance_display} currency={selectedTransaction.currency} />
                  </Typography>
                )}
                {selectedTransaction.fee_amount && (
                  <Typography>
                    <strong>Fee:</strong> {formatCurrency(selectedTransaction.fee_amount_display, selectedTransaction.fee_currency)}
                  </Typography>
                )}
                {selectedTransaction.exchange_rate && (
                  <Typography>
                    <strong>Exchange Rate:</strong> {selectedTransaction.exchange_rate}
                  </Typography>
                )}
              </Box>

              {/* Merchant Information */}
              {selectedTransaction.merchant_name && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    <StoreIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Merchant
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography><strong>Name:</strong> {selectedTransaction.merchant_name}</Typography>
                  {selectedTransaction.merchant_city && (
                    <Typography><strong>Location:</strong> {selectedTransaction.merchant_city}, {selectedTransaction.merchant_country}</Typography>
                  )}
                  {selectedTransaction.merchant_category_code && (
                    <Typography><strong>Category:</strong> {selectedTransaction.merchant_category_code}</Typography>
                  )}
                </Box>
              )}

              {/* Card Information */}
              {selectedTransaction.card_number && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    <CreditCardIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Card
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography><strong>Card:</strong> {selectedTransaction.card_number}</Typography>
                  {selectedTransaction.card_holder_first_name && (
                    <Typography>
                      <strong>Cardholder:</strong> {selectedTransaction.card_holder_first_name} {selectedTransaction.card_holder_last_name}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Counterparty Information */}
              {selectedTransaction.counterparty_name && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    <TransferIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Counterparty
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography><strong>Name:</strong> {selectedTransaction.counterparty_name}</Typography>
                  {selectedTransaction.counterparty_account_id && (
                    <Typography><strong>Account:</strong> {selectedTransaction.counterparty_account_id}</Typography>
                  )}
                  {selectedTransaction.counterparty_account_type && (
                    <Typography><strong>Type:</strong> {selectedTransaction.counterparty_account_type}</Typography>
                  )}
                </Box>
              )}

              {/* Description & Reference */}
              {(selectedTransaction.description || selectedTransaction.reference) && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Additional Details</Typography>
                  <Divider sx={{ my: 1 }} />
                  {selectedTransaction.description && (
                    <Typography><strong>Description:</strong> {selectedTransaction.description}</Typography>
                  )}
                  {selectedTransaction.reference && (
                    <Typography><strong>Reference:</strong> {selectedTransaction.reference}</Typography>
                  )}
                </Box>
              )}

              {/* Raw JSON Data */}
              {selectedTransaction.raw_data && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Raw JSON Data</Typography>
                    <Button
                      size="small"
                      onClick={() => setShowRawJson(!showRawJson)}
                      variant="outlined"
                    >
                      {showRawJson ? 'Hide' : 'Show'} Raw JSON
                    </Button>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  {showRawJson && (
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto', bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {typeof selectedTransaction.raw_data === 'string' 
                          ? JSON.stringify(JSON.parse(selectedTransaction.raw_data), null, 2)
                          : JSON.stringify(selectedTransaction.raw_data, null, 2)}
                      </pre>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => {
            setDetailsOpen(false);
            setShowRawJson(false); // Reset raw JSON visibility when closing
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Historical Sync Progress Modal */}
      <Dialog
        open={progressModalOpen && !(syncStatus === 'running' && syncProgress?.indeterminate)}
        onClose={() => {
          // Allow closing, but warn if sync is still running
          if (syncProgress?.status === 'running') {
            if (window.confirm('Sync is still in progress. Close the modal? (Sync will continue in the background)')) {
              setProgressModalOpen(false);
            }
          } else {
            setProgressModalOpen(false);
          }
        }}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={false}
      >
        <DialogTitle>
          {syncType === 'historical' ? 'Historical Sync Progress' : 'Sync Progress'}
          {syncProgress && (syncProgress.status === 'completed' || syncProgress.status === 'partial') && (
            <Chip
              label={syncProgress.status === 'partial' ? 'Partial' : 'Full'}
              size="small"
              color={syncProgress.status === 'partial' ? 'warning' : 'success'}
              sx={{ ml: 2 }}
            />
          )}
          {syncType && (
            <Chip 
              label={syncType === 'historical' ? 'Full Historical' : 'Incremental'} 
              size="small" 
              color={syncType === 'historical' ? 'warning' : 'success'}
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {!syncProgress ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Starting historical sync...
              </Typography>
            </Box>
          ) : syncProgress.status === 'not_running' ? (
            <Alert severity="info">
              {syncProgress.message || 'No historical sync in progress'}
            </Alert>
          ) : (
            <Box>
              {syncProgress.status === 'running' && syncProgress.indeterminate ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 2,
                    gap: 2,
                    textAlign: 'center',
                  }}
                >
                  <CircularProgress size={40} />
                  <Typography variant="body1" sx={{ maxWidth: 520, textAlign: 'center' }}>
                    Syncing with Revolut for IE + UK...
                  </Typography>
                </Box>
              ) : (
                <>
              {(Number(syncProgress.total_weeks) > 0 || syncType === 'historical') && (
              <Box sx={{ mb: 3 }}>
                {/* Week-based / historical: show bar. Incremental (no weeks) skips misleading 0% / Week 0. */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Overall Progress
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {Math.round(syncProgress.progress_percentage || 0)}%
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 8, bgcolor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${syncProgress.progress_percentage || 0}%`,
                      height: '100%',
                      bgcolor: syncProgress.status === 'failed' ? 'error.main' : 'primary.main',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Week {syncProgress.current_week?.week_number || 0} of {syncProgress.total_weeks || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {syncProgress.total_transactions || 0} transactions synced
                  </Typography>
                </Box>
                {syncProgress.elapsed_seconds && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Elapsed time: {Math.round(syncProgress.elapsed_seconds / 60)} minutes
                  </Typography>
                )}
              </Box>
              )}

              {/* Current Week */}
              {syncProgress.current_week && syncProgress.status === 'running' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    Current Week:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">
                      {new Date(syncProgress.current_week.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} to {new Date(syncProgress.current_week.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Typography>
                    <Chip
                      label="Processing..."
                      size="small"
                      color="primary"
                    />
                  </Box>
                </Box>
              )}

              {/* Recent Progress */}
              {syncProgress.completed_weeks && syncProgress.completed_weeks.length > 0 && (
                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    Recent Progress:
                  </Typography>
                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {syncProgress.completed_weeks.slice(-10).reverse().map((week: any, index: number) => (
                      <Box
                        key={`completed-${week.week_number}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          px: 2,
                          mb: 0.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1
                        }}
                      >
                        <Typography variant="body2">
                          Week {week.week_number}: {new Date(week.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} to {new Date(week.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </Typography>
                        <Chip
                          label={`${week.transaction_count || 0} transactions`}
                          size="small"
                          color={week.transaction_count > 0 ? 'success' : 'default'}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Success Message */}
              {syncProgress.status === 'completed' && syncProgress.message && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {syncProgress.message.split('\n').map((line: string, i: number) => (
                    <Typography key={i} variant="body2">
                      {line}
                    </Typography>
                  ))}
                </Alert>
              )}

              {/* Error Message */}
              {syncProgress.status === 'failed' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    {(() => {
                      let errorMsg = syncProgress.error || 'Historical sync failed';
                      // Truncate if too long
                      if (errorMsg.length > 200) {
                        errorMsg = errorMsg.substring(0, 200) + '...';
                      }
                      // Remove HTML tags if present
                      errorMsg = errorMsg.replace(/<[^>]*>/g, '');
                      return errorMsg;
                    })()}
                  </Typography>
                  {syncProgress.failed_weeks && syncProgress.failed_weeks.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        Failed Weeks:
                      </Typography>
                      {syncProgress.failed_weeks.slice(0, 5).map((week: any, index: number) => {
                        // Truncate error message if it's too long (e.g., HTML responses)
                        let errorMsg = week.error || 'Unknown error';
                        if (errorMsg.length > 200) {
                          errorMsg = errorMsg.substring(0, 200) + '...';
                        }
                        // Remove HTML tags if present
                        errorMsg = errorMsg.replace(/<[^>]*>/g, '');
                        
                        return (
                          <Typography key={index} variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                            • Week {week.week_number} ({new Date(week.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} to {new Date(week.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}): {errorMsg}
                          </Typography>
                        );
                      })}
                      {syncProgress.failed_weeks.length > 5 && (
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1, fontStyle: 'italic' }}>
                          ... and {syncProgress.failed_weeks.length - 5} more failed weeks
                        </Typography>
                      )}
                    </Box>
                  )}
                </Alert>
              )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {(() => {
            // Debug logging
            if (process.env.NODE_ENV === 'development') {
              console.log('Sync Progress Modal - Status:', syncProgress?.status, 'isSyncing:', isSyncing, 'syncProgress:', syncProgress);
            }
            // Keep Stop visible for non-terminal states so users can stop
            // background syncs even if progress status is briefly stale.
            const status = (syncProgress?.status || '').toLowerCase();
            const isTerminal = ['completed', 'failed', 'stopped', 'partial'].includes(status);
            const shouldShowStopButton = !isTerminal;
            return shouldShowStopButton ? (
              <Button
                onClick={async () => {
                  if (window.confirm('Are you sure you want to stop the sync? Progress will be saved and you can resume later.')) {
                    await stopSync();
                  }
                }}
                color="error"
                variant="contained"
                sx={{ mr: 'auto' }}
              >
                Stop Sync
              </Button>
            ) : (
              <Box sx={{ flex: 1 }} /> // Spacer when button is not shown
            );
          })()}
          <Button
            onClick={() => {
              if (syncProgress?.status === 'running') {
                if (window.confirm('Sync is still in progress. Close the modal? (Sync will continue in the background)')) {
                  setProgressModalOpen(false);
                }
              } else {
                setProgressModalOpen(false);
              }
            }}
            variant="contained"
            color="primary"
          >
            {syncProgress?.status === 'completed' || syncProgress?.status === 'failed' || syncProgress?.status === 'stopped' || syncProgress?.status === 'partial' ? 'Close' : 'Close (Sync continues in background)'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={indeterminateSyncToastOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={APP_TOP_TOAST_SNACKBAR_SX}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={16} color="inherit" />}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                size="small"
                color="inherit"
                variant="outlined"
                onClick={async () => {
                  await stopSync();
                  setIndeterminateSyncToastOpen(false);
                }}
              >
                Stop Sync
              </Button>
              <IconButton
                aria-label="dismiss sync status"
                color="inherit"
                size="small"
                onClick={() => setIndeterminateSyncToastOpen(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          sx={{ alignItems: 'flex-start' }}
        >
          <Stack spacing={0.35}>
            <Typography variant="body2">
              {syncProgress?.message || 'Syncing Revolut transactions for IE + UK...'}
            </Typography>
            <ToastCodeCaption
              jobId={
                syncProgress && 'job_id' in syncProgress
                  ? String((syncProgress as { job_id?: string }).job_id || '').trim() || undefined
                  : undefined
              }
              copyBody={syncProgress?.message || 'Syncing Revolut transactions for IE + UK...'}
            />
          </Stack>
        </Alert>
      </Snackbar>
      <TxLinkExistingDocumentDialog
        open={!!docLinkTx}
        transactionId={docLinkTx ? Number((docLinkTx as any).id) : null}
        onClose={() => setDocLinkTx(null)}
        onLinked={({ transactionId, documentType }) => {
          const label =
            documentType === 'receipt'
              ? 'Receipt'
              : documentType === 'sales_invoice'
                ? 'Sales invoice'
                : 'Purchase invoice';
          setDocUploadSuccess(`${label} linked to TX ${transactionId}.`);
          void refreshResolvedDocs(revolutTransactions || []);
        }}
      />
      <TxDocumentUploadDialog
        open={!!docUploadTx}
        transaction={
          docUploadTx
            ? {
                id: (docUploadTx as any).id,
                description: (docUploadTx as any).description,
                dateIso: txUploadDateIso(docUploadTx),
                supplier:
                  effectiveMerchantName(docUploadTx as any) || (docUploadTx as any).description,
              }
            : null
        }
        initialDocType={focusDocType}
        onClose={() => setDocUploadTx(null)}
        onUploaded={({ transactionId, documentType }) => {
          const label =
            documentType === 'receipt'
              ? 'Receipt'
              : documentType === 'sales_invoice'
                ? 'Sales invoice'
                : 'Purchase invoice';
          setDocUploadSuccess(`${label} uploaded and linked to TX ${transactionId}.`);
          void refreshResolvedDocs(revolutTransactions || []);
        }}
      />
      <TxDocumentPreviewDialog
        open={!!previewDoc?.open_url}
        document={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
      <Snackbar
        open={!!docUploadSuccess}
        autoHideDuration={6000}
        onClose={() => setDocUploadSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={APP_TOP_TOAST_SNACKBAR_SX}
      >
        <Alert severity="success" onClose={() => setDocUploadSuccess('')}>
          {docUploadSuccess}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Transactions;
